import { ref, reactive } from "vue";
import router from "@/router/index";
import i18n from "@/i18n/index";

import {
  Activity,
  ActivityResponse,
  ActivityRequest,
} from "@/interfaces/activity";
import { Pack, PackResponse, PackRequest } from "@/interfaces/pack";
import { SubscriptionParticipationsResponse } from "@/interfaces/participation";

import {
  Presence,
  PresenceResponse,
  PresenceRequest,
} from "@/interfaces/presence";
import {
  PublicSession,
  PublicSessionResponse,
  OnePublicSessionResponse,
  PublicSessionRequest,
  DayScheduleResponse,
} from "@/interfaces/publicSession";

import {
  Subscription,
  SubscriptionResponse,
  SubscriptionRequest,
} from "@/interfaces/subscription";
import { Fee, FeeResponse, FeeRequest } from "@/interfaces/fee";

import {
  CashJournal,
  CashJournalResponse,
  CashJournalRequest,
} from "@/interfaces/cashJournal";
import { Support, SupportResponse, SupportRequest } from "@/interfaces/support";
import {
  Assurance,
  AssuranceResponse,
  AssuranceRequest,
  MemberAssuranceResponse,
} from "@/interfaces/assurance";
import {
  PromoPack,
  PromoPackResponse,
  PromoPackRequest,
} from "@/interfaces/promoPack";
import { Bilan, BilanResponse, BilanRequest } from "@/interfaces/bilan";

import type { SessionBookingResponse } from "@/interfaces/sessionBooking";

import {
  Pointeuse,
  PointeuseResponse,
  PointeuseRequest,
} from "@/interfaces/pointeuse";

import {
  packClub,
  packClubResponse,
  packClubRequest,
} from "@/interfaces/packClub";
import {
  clubSubscriptionType,
  clubSubscriptionTypeResponse,
  clubSubscriptionTypeRequest,
} from "@/interfaces/clubSubscriptionType";
import {
  Club,
  ClubResponse,
  ClubRequest,
  SingleClubResponse,
} from "@/interfaces/club";
import {
  ficheMemberResponse,
  MemberSummaryResponse,
  MemberResponse,
} from "@/interfaces/member";
import {
  Payment,
  PaymentResponse,
  PaymentRequest,
  summaryTransactionResponse,
  clubPaymentStatisticsResponse,
} from "@/interfaces/payment";

import {
  Trainer,
  TrainerResponse,
  OneTrainerResponse,
  TrainerRequest,
  TrainerPaymentResponse,
  summaryTrainerTransactionResponse,
  TrainerPaymentRequest,
  TrainerPayment,
} from "@/interfaces/trainer";

import {
  SubscriptionMember,
  SubscriptionMemberResponse,
  LastSubscriptionMemberResponse,
  SubscriptionMemberRequest,
  summaryPaymentResponse,
  detailsSubscriptionResponse,
  RevenuOverviewResponse,
} from "@/interfaces/subscriptionMember";
import {
  SubscriptionAmountSalesResponse,
  PackCountSalesResponse,
  RegulationAmountResponse,
  IncomeRegulationAmountResponse,
  ExpenseRegulationAmountResponse,
  TrainerRegulationAmountResponse,
} from "@/interfaces/stats";

/**Incomes and expenses */
import {
  IncomeCategory,
  IncomeResponse,
  SingleIncomeResponse,
  IncomeCategoryResponse,
  IncomeCategoryRequest,
  IncomeServiceResponse,
  IncomePaymentResponse,
} from "@/interfaces/income";
import {
  ExpenseCategory,
  ExpenseResponse,
  SingleExpenseResponse,
  ExpenseCategoryResponse,
  ExpenseCategoryRequest,
  ExpenseServiceResponse,
  ExpensePaymentResponse,
} from "@/interfaces/expense";

import {
  Permission,
  PermissionResponse,
  PermissionRequest,
  AffectedPermissionRequest,
  AffectedPermissionResponse,
} from "@/interfaces/permission";
import { User, UserResponse, UserRequest } from "@/interfaces/user";

import {
  Announcement,
  AnnouncementResponse,
  AnnouncementRequest,
} from "@/interfaces/announcement";

import {
  Product,
  Category,
  ProductResponse,
  CategoryResponse,
  Sale,
  SaleResponse,
  StockMovement,
  StockMovementResponse,
} from "@/interfaces/product";

// Create a reactive array to hold the packs
export const activities = reactive<ActivityResponse>({
  data: [],
});
export const packs = reactive<PackResponse>({
  data: [],
});
export const subscriptions = reactive<SubscriptionResponse>({
  data: [],
});
export const assurances = reactive<AssuranceResponse>({
  data: [],
});
export const promopacks = reactive<PromoPackResponse>({
  data: [],
});

export const bilans = reactive<BilanResponse>({
  data: [],
});
export const sessionBookings = reactive<SessionBookingResponse>({
  data: [],
});

export const fees = reactive<FeeResponse>({
  data: [],
});
export const cash_journals = reactive<CashJournalResponse>({
  data: [],
});
export const supports = reactive<SupportResponse>({
  data: [],
});
export const pointeuses = reactive<PointeuseResponse>({
  data: [],
});
export const members = reactive<MemberResponse>({
  data: [],
});
export const members_pointeuses_errors = reactive<MemberResponse>({
  data: [],
});
export const fiche_member = reactive<ficheMemberResponse>({
  data: null,
});
export const members_assurances = reactive<MemberAssuranceResponse>({
  data: [],
});

export const member_summary = reactive<MemberSummaryResponse>({
  data: null,
});
export const transactions = reactive<PaymentResponse>({
  data: [],
});
export const club_statistic_encaisse = reactive<clubPaymentStatisticsResponse>({
  today: "",
  month: "",
  year: "",
  value: {
    today_total: 0,
    month_total: 0,
    year_total: 0,
  },
});
export const summary_transactions = reactive<summaryTransactionResponse>({
  data: [],
});
export const transactions_due_date = reactive<PaymentResponse>({
  data: [],
});
export const expired_subscriptions = reactive<SubscriptionMemberResponse>({
  data: [],
});
export const subscriptions_expiring = reactive<SubscriptionMemberResponse>({
  data: [],
});

export const last_subscription_member =
  reactive<LastSubscriptionMemberResponse>({
    data: null,
  });
export const revenu_overview = reactive<RevenuOverviewResponse>({
  today: "",
  month: "",
  year: "",
  value: {
    today_total: 0,
    month_total: 0,
    year_total: 0,
  },
});
export const clubs = reactive<ClubResponse>({
  data: [],
});

export const singleClub = reactive<SingleClubResponse>({
  data: null,
});

export const clubSubscriptionsType = reactive<clubSubscriptionTypeResponse>({
  data: [],
});

export const packsClub = reactive<packClubResponse>({
  data: [],
});

export const trainers = reactive<TrainerResponse>({
  data: [],
});

export const trainer = reactive<OneTrainerResponse>({
  data: null,
});
export const trainersPayments = reactive<TrainerPaymentResponse>({
  data: [],
});
export const summary_trainersPayments =
  reactive<summaryTrainerTransactionResponse>({
    data: [],
  });
export const trainer_regulations_amount =
  reactive<TrainerRegulationAmountResponse>({
    data: [],
    total: 0,
  });

/**Presences */
export const presences = reactive<PresenceResponse>({
  data: [],
});
/**Sessions */
export const public_sessions = reactive<PublicSessionResponse>({
  data: [],
});
export const public_session = reactive<OnePublicSessionResponse>({
  data: null,
});

export const public_sessions_table = reactive<DayScheduleResponse>({
  data: [],
});

/**
 * Subscription actifs & inactifs
 */
// historiques_subscriptions with payements

export const details_subscription = reactive<detailsSubscriptionResponse>({
  data: null,
});
export const subscriptions_members = reactive<SubscriptionMemberResponse>({
  data: [],
});
export const summary_subscriptions = reactive<summaryPaymentResponse>({
  data: {},
});

export const total_encaissements = ref<number>(0);
export const total_decaissements = ref<number>(0);
export const total_cashflow = ref<number>(0);

export const total_daily_cash_report = ref<number>(0);
export const members_count = ref<number>(0);

export const members_with_pointeuse_errors_count = ref<number>(0);
export const count_subscriptions_inactive = ref<number>(0);
export const count_members_dosent_have_subscription = ref<number>(0);
export const count_members_canceled_subscription = ref<number>(0);
export const count_members_paused_subscription = ref<number>(0);
export const count_members_not_started_subscription = ref<number>(0);
export const count_renewed_today_subscriptions = ref<number>(0);
export const count_new_today_subscriptions = ref<number>(0);
export const count_subscriptions_active = ref<number>(0);
export const transactions_due_date_count = ref<number>(0);
export const expired_subscriptions_count = ref<number>(0);
export const subscriptions_expiring_count = ref<number>(0);
export const syncTest = ref<any>(null);
export const hikvisionAttendance = ref<any>(null);

export const statusMember = ref<any[]>([
  { id: "true", name: i18n.t("status.active") },
  { id: "false", name: i18n.t("status.expired") },
  { id: "null", name: i18n.t("status.subscription_not_found") },
  { id: "canceled", name: i18n.t("status.canceled") },
  { id: "paused", name: i18n.t("status.paused") },
  { id: "not_started", name: i18n.t("status.not_started") },
  { id: "not_paid_yet", name: i18n.t("status.not_paid_yet") },
]);
export const statusPointeuse = ref<any[]>([
  { id: "all", name: i18n.t("status.pointeuse_status.all") },
  { id: "synced", name: i18n.t("status.pointeuse_status.synced") },
  { id: "en_attent", name: i18n.t("status.pointeuse_status.pending_sync") },
  { id: "face_missing", name: i18n.t("status.pointeuse_status.face_missing") },
]);
export const statusImage = ref<any[]>([
  { id: ".webp", name: "Image avec extension .webp" },
  { id: ".png", name: "Image avec extension .png" },
  { id: ".jpg", name: "Image avec extension .jpg" },
  { id: ".jpeg", name: "Image avec extension .jpeg" },
  { id: "null", name: "Les adhérents qui n'ont pas d'image" },
]);
export const genders = ref<any[]>([
  { id: 0, name: i18n.t("tables.genders.female") },
  { id: 1, name: i18n.t("tables.genders.male") },
  { id: 2, name: i18n.t("tables.genders.boy") },
  { id: 3, name: i18n.t("tables.genders.girl") },
]);
export const targetAge = ref<any[]>([
  { id: "adult", name: i18n.t("tables.target_age.adult") },
  { id: "minor", name: i18n.t("tables.target_age.minor") },
  { id: "both", name: i18n.t("tables.target_age.both") },
]);

export const discountTypes = ref<any[]>([
  {
    id: "percentage",
    name: i18n.t("tables.subscription_member.discount_type.percentage"),
  },
  {
    id: "amount",
    name: i18n.t("tables.subscription_member.discount_type.amount"),
  },
]);


export const bank_names = ref<any[]>([]);
export const payment_methods = ref<any[]>([]);
export const specializations = ref<any[]>([]);
export const paymentsTypes = ref<any[]>([]);
export const personTypes = ref<any[]>([
  { id: "seller", name: i18n.t("tables.trainer.person_types.seller") },
  { id: "coach", name: i18n.t("tables.trainer.person_types.coach") },
  { id: "security", name: i18n.t("tables.trainer.person_types.security") },
  {
    id: "housemaid",
    name: i18n.t("tables.trainer.person_types.housemaid"),
  },
  { id: "director", name: i18n.t("tables.trainer.person_types.director") },
  {
    id: "reception",
    name: i18n.t("tables.trainer.person_types.reception"),
  },
  { id: "manager", name: i18n.t("tables.trainer.person_types.manager") },
  {
    id: "maintenance",
    name: i18n.t("tables.trainer.person_types.maintenance"),
  },
  {
    id: "accountant",
    name: i18n.t("tables.trainer.person_types.accountant"),
  },
  {
    id: "other",
    name: i18n.t("tables.trainer.person_types.other"),
  },
]);
export const contractsTypes = ref<any[]>([]);
export const cancellationTypes = ref<any[]>([]);
export const device_brands = ref<any[]>([]);
export const supported_auth_methods = ref<any[]>([]);
export const paginationLinks = ref<any>({});
export const payementStatus = ref<any[]>([
  { id: "paid", name: "Paiement encaissé" },
  { id: "scheduled", name: "Paiement programmé (En attente d’encaissement)" },
]);
export const subscriptinPaiementStatus = ref<any[]>([
  { id: "true", name: i18n.t("status.paid") },
  { id: "false", name: i18n.t("status.unpaid") },
]);
export const subscriptionRenewalStatus = ref<any[]>([
  {
    id: "first_subscription",
    name: i18n.t("status.renewal.first_subscription"),
  }, // أول اشتراك
  { id: "renewed", name: i18n.t("status.renewal.renewed") }, // مجدد (Renouvelé)
]);
export const memberRenewalStatus = ref<any[]>([
  { id: "renewed", name: i18n.t("status.renewal.renewed") }, // مجدد (Renouvelé)
  { id: "not_renewed", name: i18n.t("status.renewal.not_renewed") }, // مجدد (Renouvelé)
]);
export const statisticScope = ref([
  { id: "first", name: i18n.t("status.scope.first") },
  { id: "last", name: i18n.t("status.scope.last") },
  { id: "all", name: i18n.t("status.scope.all") },
]);

export const filterStatsByPromo = ref([
  { id: "with", name: i18n.t("tables.subscription_sold.promo_options.with") },
  {
    id: "without",
    name: i18n.t("tables.subscription_sold.promo_options.without"),
  },
]);
export const filterStatsGroupBy = ref([
  { id: "pack", name: i18n.t("tables.subscription_sold.stats_group_by.pack") },
  {
    id: "seller",
    name: i18n.t("tables.subscription_sold.stats_group_by.seller"),
  },
]);

const getItemClub = localStorage.getItem("selectedClub") ?? null;
const parseClub = getItemClub ? JSON.parse(getItemClub) : null;
export const clubUUID = ref(parseClub?.uuid || null);
export const currentClub = ref<Club>(parseClub || {});

const strorageUserUUID = localStorage.getItem("selectedUserUUID") ?? "";
export const userUUID = ref(strorageUserUUID || null);
export const loggedIn = ref(strorageUserUUID ? true : false);
const getUser = localStorage.getItem("user") ?? null;
const parseUser = getUser ? JSON.parse(getUser) : null;
//export const user = ref(parseUser?.data || null);
export const user = ref<User>(parseUser?.data || {});

export const reloadQrCodeScan = ref<boolean | false>(false);
/**
 * Statistiques
 *
 */
export const subscriptions_amount_sales =
  reactive<SubscriptionAmountSalesResponse>({
    data: [],
    total: 0,
  });
export const subscriptions_amount_sales_by_day =
  reactive<SubscriptionAmountSalesResponse>({
    data: [],
    total: 0,
  });
export const packs_count_sales = reactive<PackCountSalesResponse>({
  data: [],
  total_count: 0,
  total_amount: 0,
});
export const regulations_amount = reactive<RegulationAmountResponse>({
  data: [],
  total: 0,
});
export const today_regulations_amount = reactive<RegulationAmountResponse>({
  data: [],
  total: 0,
});
export const regulations_amount_by_day = reactive<RegulationAmountResponse>({
  data: [],
  total: 0,
});

/**
 * Incomes
 */
export const incomesCategories = reactive<IncomeCategoryResponse>({
  data: [],
});
export const incomesServices = reactive<IncomeServiceResponse>({
  data: [],
});
export const incomesPayments = reactive<IncomePaymentResponse>({
  data: [],
});
export const incomes = reactive<IncomeResponse>({
  data: [],
});
export const singleIncome = reactive<SingleIncomeResponse>({
  data: null,
});
export const income_regulations_amount =
  reactive<IncomeRegulationAmountResponse>({
    data: [],
    total: 0,
  });

export const income_regulations_amount_by_day =
  reactive<IncomeRegulationAmountResponse>({
    data: [],
    total: 0,
  });
export const summary_incomes = reactive<summaryPaymentResponse>({
  data: {},
});
/**
 * Expenses
 */
export const expensesCategories = reactive<ExpenseCategoryResponse>({
  data: [],
});
export const expensesServices = reactive<ExpenseServiceResponse>({
  data: [],
});
export const expensesPayments = reactive<ExpensePaymentResponse>({
  data: [],
});
export const expenses = reactive<ExpenseResponse>({
  data: [],
});
export const singleExpense = reactive<SingleExpenseResponse>({
  data: null,
});
export const expense_regulations_amount =
  reactive<ExpenseRegulationAmountResponse>({
    data: [],
    total: 0,
  });
export const expense_regulations_amount_by_day =
  reactive<ExpenseRegulationAmountResponse>({
    data: [],
    total: 0,
  });
export const summary_expenses = reactive<summaryPaymentResponse>({
  data: {},
});
// permissions
export const permissions = reactive<PermissionResponse>({
  data: [],
});
// permissions
export const userPermissions = reactive<AffectedPermissionResponse>({
  data: [],
});

/**
 * Users
 */
// permissions
export const users = reactive<UserResponse>({
  data: [],
});

// Announcements
export const announcements = reactive<AnnouncementResponse>({
  data: [],
});

/**
 * Products & Stock
 */
export const productCategories = reactive<CategoryResponse>({
  data: [],
});
export const categories = reactive<CategoryResponse>({
  data: [],
});
export const products = reactive<ProductResponse>({
  data: [],
});
export const stockMovements = reactive<StockMovementResponse>({
  data: [],
});
export const sales = reactive<SaleResponse>({
  data: [],
});

/**
 * Participations (Group familly)
 */
export const participations = reactive<SubscriptionParticipationsResponse>({
  data: [],
});

export const bookingStatuses = ref([
  "confirmed",
  "pending",
  "canceled",
  "no_show",
]);
export const attendanceStatuses = ref(["pending", "completed", "absent"]);

// Togle sidebar menu
export const isTogglerActive = ref(false);
export const isSidebarFolded = ref(false);
export const isSidebarOpen = ref(false);

export const closeSidebar = () => {
  isSidebarOpen.value = false;
  isTogglerActive.value = false;
  document.body.classList.toggle("sidebar-folded", isSidebarFolded.value);
  document.body.classList.toggle("sidebar-open", isSidebarOpen.value);
};

//
const getLang = localStorage.getItem("selectedLang") ?? "fr";
export const selectedLang = ref(getLang);

export const logoImage = ref<HTMLImageElement | null>(null);
export const signatureImage = ref<HTMLImageElement | null>(null);
